import { APP_SEARCH_FAILED_MESSAGE } from '@app-state/app.const';
import { Injectable, inject } from '@angular/core';
import { Country, Hotel, SearchOnlyHotelPackagesResponse } from '@appTypes/api.types';
import { DistrictsTreeItem, IOnlyHotelPackage } from '@appTypes/redefined-types';
import { deepClean, hotelPackageExtraParamsProcess } from '@core/misc/misc.utils';
import { DataLoadStatusModel, RequestStateModel } from '@core/store/store.models';
import { RequestStateParams } from '@core/store/store.types';
import { Action, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { insertItem, patch, updateItem } from '@ngxs/store/operators';
import { emptySearchResults } from '@shared/components/providers-unavailable/empty-search-results';
import { CatalogueApiService } from '@shared/services/api/catalogue-api.service';
import { SupplierTimingTracker } from '@shared/components/search-loader/supplier-timing-tracker';
import { SEARCH_SUPPLIER_SUMMARY_ENABLED } from '@shared/components/search-loader/search-supplier-summary.component';
import { WebsiteFlags } from '@core/services/website-flags.service';
import { isEmpty } from 'lodash-es';
import { errorResponseWithTrace } from '@core/interceptors/api.interceptor';
import { EMPTY, catchError, switchMap, tap, throwError, timer } from 'rxjs';
import { AppPersistedState } from '@app-state/app.persisted.state';
import { OnlyHotelPackageResults } from '../hotels.types';
import { HotelsApiService } from '../services/hotels-api.service';
import {
  hotelsGetDestinationCountries,
  hotelsGetHotels,
  hotelsGetLocations,
  hotelsPollSearchProgress,
  hotelsReset,
  hotelsResetResult,
  hotelsSearchChildPackages,
  hotelsSearchHotels
} from './hotels.actions';
import { HotelsSearchFormDataModel, HotelsStateModel } from './hotels.model';
import { appRefreshCurrencyData } from '@app-state/app.actions';
import { isPeriodsSearch } from '../hotels-stays.util';

const defaults = new HotelsStateModel();

/** Progressive search R-PS3: the poll interval when the answer names none, and the failed polls in a row after which polling stops. */
const PROGRESS_POLL_DEFAULT_MS = 1500;
const PROGRESS_POLL_MAX_FAILURES = 3;
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

@State<HotelsStateModel>({
  name: 'hotels',
  defaults
})
@Injectable()
export class HotelsState {
  readonly #catalogueApi = inject(CatalogueApiService);
  readonly #api = inject(HotelsApiService);
  readonly #store = inject(Store);
  readonly #summaryEnabled = inject(SEARCH_SUPPLIER_SUMMARY_ENABLED);
  readonly #websiteFlags = inject(WebsiteFlags);

  /**
   * Fired by StartupService on `beforeunload` (a reload or closing the tab): clears the earlier offers and the catalogue of the country
   * (the 2 MB hotel list sat in sessionStorage next to the form) but KEEPS the search form - the agent who reloads finds the form as it was
   * (website-web#45, QA doc features/website/hotels-form-and-details-fixes-2026-10-09.md R-HF8); the catalogue is read again by the form on boot.
   */
  @Action(hotelsReset)
  hotelsReset({ setState, getState }: StateContext<HotelsStateModel>) {
    setState({ ...new HotelsStateModel(), searchForm: getState().searchForm });
  }
  @Action(hotelsSearchHotels, { cancelUncompleted: true })
  hotelsSearchHotels(
    { setState, getState, patchState, dispatch }: StateContext<HotelsStateModel>,
    { paging }: hotelsSearchHotels
  ) {
    const {
      searchResults: { loadState },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;
    const payload = deepClean(searchForm.model);
    if (isEmpty(payload)) return;

    if (paging !== undefined) {
      const pagingRecords = getState().searchResults.response?.pagingRecords;
      if (pagingRecords !== undefined) paging = { ...pagingRecords, ...paging };
    }
    // R-SRF13 (docs#443): a Search by periods answers every stay of the range; it opens ordered by stay (each stay cheapest first) so the rows group
    if (!paging?.sortByField && isPeriodsSearch(payload))
      paging = { ...paging, sortByField: 'SortCheckInOut', sortOrder: 'ASC' };
    if (!paging?.pagingId)
      patchState({
        searchResults: new RequestStateModel<OnlyHotelPackageResults>(new DataLoadStatusModel('loading')),
        childResults: [] //reset
      });
    else
      setState(
        patch<HotelsStateModel>({
          searchResults: patch<RequestStateModel<OnlyHotelPackageResults>>({
            loadState: new DataLoadStatusModel('loading')
          })
        })
      );

    const pageRowCount = paging?.pageRowCount ?? 25;
    const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
    dispatch(new appRefreshCurrencyData()); // rates every search, the currency list if it never loaded
    // R-PS2 / R-PS9: the fastest suppliers first, the rest polled; a search grouped by hotel stays one answer
    const progressive = !payload?.groupByHotel && !payload?.groupByHotelId;
    const startedAt = nowMs();
    const earlierDiagnostics = paging?.pagingId ? getState().searchResults.response?.diagnostics : undefined;

    return this.#api
      .searchHotels({ ...payload, ...paging, pageRowCount: pageRowCount, searchCurrency, progressive })
      .pipe(
        tap((response) => {
          patchState({
            searchResults:
              // R-PS3: no offer yet while suppliers are still being asked is not "Data not found"
              !paging?.pagingId && !response.packages?.length && response.searchComplete !== false
                ? emptySearchResults<OnlyHotelPackageResults>(response.providersUnavailable)
                : new RequestStateModel<OnlyHotelPackageResults>({
                    ...response,
                    diagnostics: paging?.pagingId
                      ? (response.diagnostics ?? earlierDiagnostics)
                      : this.#firstTimings(response, startedAt),
                    pagingRecords: {
                      ...paging,
                      pagingId: response.pagingId,
                      pageNumber: response.pageNumber,
                      pageCount: response.pageCount,
                      totalCount: response.totalCount
                    },
                    isGroupByHotels: Boolean(payload?.groupByHotel ?? payload?.groupByHotelId),
                    packages: this.#packagesOf(response, payload.childrenAges)
                  })
          });
          if (response.searchComplete === false && response.pagingId)
            dispatch(new hotelsPollSearchProgress(response.pagingId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS));
        }),
        catchError((e) => {
          if (!paging?.pagingId)
            patchState({
              searchResults: new RequestStateModel<OnlyHotelPackageResults>(
                new DataLoadStatusModel(
                  'error',
                  e?.displayErrorMessage ?? APP_SEARCH_FAILED_MESSAGE,
                  errorResponseWithTrace(e)
                )
              )
            });
          else
            setState(
              patch<HotelsStateModel>({
                searchResults: patch<RequestStateModel<OnlyHotelPackageResults>>({
                  loadState: new DataLoadStatusModel(
                    'error',
                    e?.displayErrorMessage ?? APP_SEARCH_FAILED_MESSAGE,
                    errorResponseWithTrace(e)
                  )
                })
              })
            );
          return throwError(() => e);
        })
      );
  }

  /**
   * Progressive search R-PS3 / R-PS4 / R-PS5a (docs features/website/hotel-search-progressive.md): asks the running search for its current page.
   * While it runs, page 1 takes the new rows - online-api merges every answered supplier's offers by price on each poll (R-PS5a, Levan
   * 09.10.2026), so a slower supplier's cheaper offers appear on page 1 as soon as it answered; on a later page only the counts and the progress
   * change, so the rows the agent reads do not jump. The poll that completes the search brings the page the agent is on. Stops when the search completes, when another search or a reset replaced it, or after PROGRESS_POLL_MAX_FAILURES
   * failed polls in a row (the progress bar then gives way to the results it has).
   */
  @Action(hotelsPollSearchProgress, { cancelUncompleted: true })
  hotelsPollSearchProgress(
    { getState, setState, dispatch }: StateContext<HotelsStateModel>,
    { pagingId, delayMs, failures }: hotelsPollSearchProgress
  ) {
    const isCurrent = () => {
      const response = getState().searchResults.response;
      return response?.pagingRecords?.pagingId === pagingId && response.searchComplete === false;
    };

    return timer(Math.max(0, delayMs)).pipe(
      switchMap(() => {
        const { searchResults, searchForm } = getState();
        const records = searchResults.response?.pagingRecords;
        if (!isCurrent() || !records || !searchForm.model || searchResults.loadState.status === 'loading') return EMPTY;

        const payload = deepClean(searchForm.model);
        const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
        return this.#api
          .searchHotels({
            ...payload,
            pagingId,
            pageNumber: records.pageNumber,
            pageRowCount: records.pageRowCount ?? 25,
            sortByField: records.sortByField,
            sortOrder: records.sortOrder,
            searchCurrency,
            progressive: true
          })
          .pipe(
            tap((response) => {
              const current = getState().searchResults.response;
              if (!isCurrent() || !current) return;
              // R-PS3 / QA #20: the poll that completes the search with no offer ends in "Data not found", like a first answer does
              if (response.searchComplete !== false && !response.packages?.length && !response.totalCount) {
                setState(
                  patch<HotelsStateModel>({
                    searchResults: emptySearchResults<OnlyHotelPackageResults>(response.providersUnavailable)
                  })
                );
                return;
              }
              const takesRows = (current.pagingRecords?.pageNumber ?? 1) <= 1 || response.searchComplete !== false;
              setState(
                patch<HotelsStateModel>({
                  searchResults: patch<RequestStateModel<OnlyHotelPackageResults>>({
                    response: {
                      ...current,
                      ...(takesRows ? { packages: this.#packagesOf(response, payload.childrenAges) } : {}),
                      searchComplete: response.searchComplete,
                      diagnostics: response.diagnostics ?? this.#measuredTimings(response),
                      providersTotal: response.providersTotal,
                      providersAnswered: response.providersAnswered,
                      providersPending: response.providersPending,
                      pollAfterMs: response.pollAfterMs,
                      providersUnavailable: response.providersUnavailable,
                      instantConfirmationUnavailable: response.instantConfirmationUnavailable,
                      instantColourBands: response.instantColourBands,
                      pageCount: response.pageCount,
                      totalCount: response.totalCount,
                      pagingRecords: {
                        ...current.pagingRecords,
                        pageCount: response.pageCount,
                        totalCount: response.totalCount
                      }
                    }
                  })
                })
              );
              if (response.searchComplete === false)
                dispatch(new hotelsPollSearchProgress(pagingId, response.pollAfterMs ?? PROGRESS_POLL_DEFAULT_MS));
            }),
            catchError(() => {
              if (!isCurrent()) return EMPTY;
              if (failures + 1 < PROGRESS_POLL_MAX_FAILURES) {
                dispatch(new hotelsPollSearchProgress(pagingId, delayMs * 2, failures + 1));
                return EMPTY;
              }
              const current = getState().searchResults.response;
              if (current)
                setState(
                  patch<HotelsStateModel>({
                    searchResults: patch<RequestStateModel<OnlyHotelPackageResults>>({
                      response: { ...current, searchComplete: true, pollAfterMs: null }
                    })
                  })
                );
              return EMPTY;
            })
          );
      })
    );
  }

  /** The rows of an answer as the result views read them (the hotel's extra params resolved for the searched children). */
  /** run 24 supplier summary: the answer's own timings when online-api sends them, else measured here between the progressive answers */
  readonly #timings = new SupplierTimingTracker();

  /**
   * Kazunion Website Flags R-WF20 (plan B-5): the browser measures supplier timings only for an agent who may see them - the environment switch
   * `searchSupplierSummary` AND the agent's `website-supplier-answers`. Before, an agent whose answers came without `diagnostics` (the panel hidden)
   * still got a summary built here from `providersUnavailable`. Display only: the search, its polling and its rows are the same either way.
   */
  #measuresTimings(): boolean {
    return this.#summaryEnabled && this.#websiteFlags.supplierAnswers();
  }

  /** the measuring of this search started with its first answer (a flag switched on later does not start it half way) */
  #measuring = false;

  #firstTimings(response: SearchOnlyHotelPackagesResponse, startedAt: number) {
    this.#measuring = false;
    if (response.diagnostics) return response.diagnostics;
    if (!response.providersTotal || !this.#measuresTimings()) return undefined;
    this.#measuring = true;
    this.#timings.start(startedAt);
    return this.#timings.observe(this.#snapshotOf(response), nowMs());
  }

  #measuredTimings(response: SearchOnlyHotelPackagesResponse) {
    return this.#measuring && this.#measuresTimings()
      ? this.#timings.observe(this.#snapshotOf(response), nowMs())
      : null;
  }

  #snapshotOf(response: SearchOnlyHotelPackagesResponse) {
    return {
      complete: response.searchComplete !== false,
      pending: response.providersPending,
      unavailable: response.providersUnavailable,
      codes: response.packages?.map((p) => p.hotel?.supplierCode)
    };
  }

  #packagesOf(response: Pick<SearchOnlyHotelPackagesResponse, 'packages'>, childrenAges: number[] | null | undefined) {
    return response.packages?.map((e) => ({
      ...e,
      hotel: e.hotel ? hotelPackageExtraParamsProcess({ childrenAges: childrenAges ?? [] }, [e.hotel])[0] : undefined
    }));
  }

  @Action(hotelsSearchChildPackages)
  hotelsSearchChildPackages(
    { getState, setState }: StateContext<HotelsStateModel>,
    { parentHotelCode, reload }: hotelsSearchChildPackages
  ) {
    const childResults = getState().childResults.find((e) => e.parentHotelCode === parentHotelCode);
    if (childResults?.results?.loadState?.status === 'loading') return;
    if (!reload && childResults?.results?.response?.packages?.length) return; // already loaded

    setState(
      patch<HotelsStateModel>({
        childResults: insertItem({
          parentHotelCode,
          results: new RequestStateModel<OnlyHotelPackageResults>(new DataLoadStatusModel('loading'))
        })
      })
    );

    const {
      searchResults: { loadState, response: searchResultsResponse },
      searchForm
    } = getState();
    if (loadState.status === 'loading') return;

    if (!searchForm.model) return;

    const payload = deepClean(searchForm.model);

    const searchCurrency = this.#store.selectSnapshot(AppPersistedState.activedCurrency);
    return this.#api
      .searchHotels({
        ...payload,
        hotelCodes: [parentHotelCode],
        searchCurrency,
        groupByHotel: false,
        groupByHotelId: false,
        pagingId: searchResultsResponse?.pagingId
      })
      .pipe(
        tap((response) => {
          setState(
            patch<HotelsStateModel>({
              childResults: updateItem((e) => e.parentHotelCode === parentHotelCode, {
                parentHotelCode,
                results: new RequestStateModel<OnlyHotelPackageResults>({
                  ...response,
                  packages: response.packages?.map((e) => ({
                    ...e,
                    hotel: e.hotel
                      ? hotelPackageExtraParamsProcess({ childrenAges: payload.childrenAges ?? [] }, [e.hotel])[0]
                      : undefined
                  }))
                })
              })
            })
          );
        }),
        catchError((e) => {
          setState(
            patch<HotelsStateModel>({
              childResults: updateItem((e) => e.parentHotelCode === parentHotelCode, {
                parentHotelCode,
                results: new RequestStateModel<OnlyHotelPackageResults>(
                  new DataLoadStatusModel(
                    'error',
                    e?.displayErrorMessage ?? APP_SEARCH_FAILED_MESSAGE,
                    errorResponseWithTrace(e)
                  )
                )
              })
            })
          );
          return throwError(() => e);
        })
      );
  }
  @Action(hotelsGetLocations, { cancelUncompleted: true })
  hotelsGetLocations({ getState, setState }: StateContext<HotelsStateModel>, { refresh, params }: hotelsGetLocations) {
    const {
      searchFormData: { locations }
    } = getState();
    if (locations.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<DistrictsTreeItem[]>) => {
      setState(
        patch<HotelsStateModel>({
          searchFormData: patch<HotelsSearchFormDataModel>({ locations: new RequestStateModel(stateParams) })
        })
      );
    };

    params = deepClean(params);

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getArrivalDistricts(params).pipe(
      tap((response) => updateState(response)),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(hotelsGetHotels, { cancelUncompleted: true })
  hotelsGetHotels({ getState, setState }: StateContext<HotelsStateModel>, { refresh, params }: hotelsGetHotels) {
    const {
      searchFormData: { hotels }
    } = getState();

    if (hotels.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Hotel[]>) => {
      setState(
        patch<HotelsStateModel>({
          searchFormData: patch<HotelsSearchFormDataModel>({ hotels: new RequestStateModel(stateParams) })
        })
      );
    };

    params = deepClean(params);

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getHotels(params).pipe(
      tap((response) => {
        response.sort((a, b) => {
          const hotelNameA = a?.hotelName?.toUpperCase();
          const hotelNameB = b?.hotelName?.toUpperCase();
          return hotelNameA && hotelNameB ? hotelNameA.localeCompare(hotelNameB) : 0;
        });
        updateState(response);
      }),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }

  @Action(hotelsGetDestinationCountries, { cancelUncompleted: true })
  hotelsGetDestinationCountries(
    { getState, setState }: StateContext<HotelsStateModel>,
    { refresh }: hotelsGetDestinationCountries
  ) {
    const {
      searchFormData: { destinationCountries }
    } = getState();

    if (destinationCountries.loadState.status === 'completed' && !refresh) return;

    const updateState = (stateParams: RequestStateParams<Country[]>) => {
      setState(
        patch<HotelsStateModel>({
          searchFormData: patch<HotelsSearchFormDataModel>({ destinationCountries: new RequestStateModel(stateParams) })
        })
      );
    };

    updateState(new DataLoadStatusModel('loading'));

    return this.#catalogueApi.getDestinationCountries({ onlyHotels: true }).pipe(
      tap((response) => {
        setState(
          patch<HotelsStateModel>({
            searchFormData: patch<HotelsSearchFormDataModel>({
              destinationDefaultCountry: response?.defaultCountryCode ?? null
            })
          })
        );
        updateState(response?.countries ?? []);
      }),
      catchError((e) => {
        updateState(
          new DataLoadStatusModel(
            'error',
            "It seems that something has gone wrong. Don't worry, our support team is ready to help you",
            errorResponseWithTrace(e)
          )
        );
        return throwError(() => e);
      })
    );
  }
  @Action(hotelsResetResult)
  hotelsResetResult({ patchState }: StateContext<HotelsStateModel>) {
    patchState({
      searchResults: new RequestStateModel<OnlyHotelPackageResults>()
    });
  }

  /* Selectors */

  @Selector([HotelsState])
  static destinationCountries({ searchFormData: { destinationCountries } }: HotelsStateModel) {
    return destinationCountries;
  }

  @Selector([HotelsState])
  static destinationDefaultCountry({ searchFormData: { destinationDefaultCountry } }: HotelsStateModel) {
    return destinationDefaultCountry;
  }

  static findDestinationCountryName(code: string) {
    return createSelector(
      [HotelsState],
      ({ searchFormData: { destinationCountries } }: HotelsStateModel): string | null => {
        if (!destinationCountries.response?.length) return null;
        return destinationCountries.response.find((c) => c.countryCode === code)?.countryName ?? null;
      }
    );
  }

  static getHotelExtraParams(id: string) {
    return createSelector([HotelsState], ({ searchResults: { response } }: HotelsStateModel) => {
      return (response?.packages as IOnlyHotelPackage[])?.find((e) => e.id === id)?.hotel?._extraParams;
    });
  }

  static getChildsOfHotel(parentHotelCode: string) {
    return createSelector([HotelsState], (state: HotelsStateModel) => {
      return state.childResults.find((e) => e.parentHotelCode === parentHotelCode) ?? null;
    });
  }

  @Selector([HotelsState])
  static resultIsGroupByHotels({ searchResults: { response } }: HotelsStateModel) {
    return Boolean(response?.isGroupByHotels);
  }

  @Selector([HotelsState])
  static childResults({ childResults }: HotelsStateModel) {
    return childResults;
  }
  @Selector([HotelsState])
  static searchFormData({ searchForm: { model } }: HotelsStateModel) {
    return model;
  }
  @Selector([HotelsState])
  static searchResults({ searchResults }: HotelsStateModel) {
    return searchResults;
  }
  @Selector([HotelsState])
  static isResult({ searchResults }: HotelsStateModel) {
    return Boolean(searchResults?.response?.packages?.length);
  }
  @Selector([HotelsState])
  static locations({ searchFormData: { locations } }: HotelsStateModel) {
    return locations;
  }
  @Selector([HotelsState])
  static hotels({ searchFormData: { hotels } }: HotelsStateModel) {
    return hotels;
  }
  @Selector([HotelsState])
  static searchLoadStatus({ searchResults: { loadState } }: HotelsStateModel): DataLoadStatusModel {
    return loadState;
  }
  /**
   * Levan, 08.10.2026: the search window gives way only when the first offers are there to read - a progressive search whose first
   * answer brought no row yet (its fastest supplier had none, or online-api's first-answer wait ran out) keeps the window until a
   * poll brings rows, until the search completes (then the empty result or its notice shows) or until it failed.
   */
  @Selector([HotelsState])
  static searchFirstBatchReady({ searchResults: { loadState, response } }: HotelsStateModel): boolean {
    if (loadState.status === 'loading') return false;
    if (loadState.status === 'error') return true;
    return Boolean(response?.packages?.length) || response?.searchComplete !== false;
  }
}
